import type { NarrationOptions, NarrationService } from '../../sdk/narration';
import { AUDIO_TUNABLES } from '../config/audio';
import type { AudioService } from './audio-service';

/**
 * Narration ducks the music, at the platform layer and nowhere else (PS5-2, D-094(3)).
 *
 * **Why a wrapper, and not a listener on the narration-activity seam.** `observeNarration` reports
 * a line the moment it is *requested*, which is exactly right for the repeat readout it exists for
 * and useless here: ducking needs both edges, and "it was asked for" says nothing about when the
 * voice stopped. The one thing in this codebase that already knows when a line is over is
 * `NarrationService.speak`'s own promise — `BrowserTtsNarrationService` settles it on `onend`, on
 * `onerror`, on cancellation, on a watchdog give-up, and immediately when narration is disabled or
 * no voice exists. So the duck is held for the life of that promise, in a `finally`, which makes
 * the release **structural**: every path the speech engine can take is a path that releases,
 * including the ones nobody thought of.
 *
 * The wrapper decides nothing about speech and changes nothing about it: same text, same options,
 * same promise, same errors. It composes with `observeNarration` in either order.
 *
 * SFX are absent from this file on purpose. Nothing about a short cue claims the foreground —
 * that is a property of the priorities the manifest declares, not a rule written twice.
 */

/**
 * The narration service shape the shell holds: the contract plus the browser-availability flag the
 * grown-up screens report. Preserved through the wrapper, because a wrapped service that lost
 * `available` would make the setup screen claim a device has no voice.
 */
export type AvailableNarrationService = NarrationService & { readonly available: boolean };

export function duckMusicUnderNarration<T extends NarrationService>(
  inner: T,
  audio: AudioService,
  priority?: number,
): T extends AvailableNarrationService ? AvailableNarrationService : NarrationService;
export function duckMusicUnderNarration(
  inner: NarrationService,
  audio: AudioService,
  priority: number = AUDIO_TUNABLES.narrationForegroundPriority,
): NarrationService {
  const wrapper: AvailableNarrationService = {
    get available(): boolean {
      return (inner as Partial<AvailableNarrationService>).available ?? false;
    },

    async speak(text: string, options?: NarrationOptions): Promise<void> {
      const release = audio.claimForeground(priority);
      try {
        await inner.speak(text, options);
      } finally {
        release();
      }
    },

    stop(): void {
      inner.stop();
    },

    async replayLast(): Promise<void> {
      const release = audio.claimForeground(priority);
      try {
        await inner.replayLast();
      } finally {
        release();
      }
    },

    setEnabled(enabled: boolean): void {
      inner.setEnabled(enabled);
    },

    setVolume(volume: number): void {
      inner.setVolume(volume);
    },

    setVoiceProfile(profile: string): void {
      inner.setVoiceProfile(profile);
    },
  };

  return wrapper;
}
